import React, {useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {Icon} from '@forecast-it/design-system';
import {NOVA_PURPLE, ERROR_COLOR_2} from './colors';
import type {AttachedFile, SowExtractionStep} from './types';
import {SOW_CHECKLIST_LABELS} from './constants';
import {
	ExtractionBody,
	ExtractionCard,
	FileCard,
	FileCardIcon,
	FileCardMeta,
	FileCardName,
	FileCardSize,
	FileCardReplace,
	ExtractionStatus,
	SpinnerIcon,
	ChecklistContainer,
	ChecklistRow,
	ChecklistDot,
	ChecklistLabel,
} from './styles';

interface SowExtractionProgressProps {
	file: AttachedFile;
	steps: SowExtractionStep[];
	onReplace: (file: AttachedFile) => void;
}

function formatBytes(base64: string): string {
	const bytes = Math.round((base64.length * 3) / 4);
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const SowExtractionProgress: React.FC<SowExtractionProgressProps> = ({file, steps, onReplace}) => {
	const {formatMessage} = useIntl();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [replaceError, setReplaceError] = useState<string | null>(null);

	const handleReplaceFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		const f = e.target.files?.[0];
		if (!f) return;
		setReplaceError(null);
		if (f.size > 20 * 1024 * 1024) {
			setReplaceError(formatMessage({id: 'sow_upload.file_too_large'}));
			if (fileInputRef.current) fileInputRef.current.value = '';
			return;
		}
		const reader = new FileReader();
		reader.onload = evt => {
			const b64 = (evt.target?.result as string).split(',')[1] ?? '';
			if (fileInputRef.current) fileInputRef.current.value = '';
			onReplace({filename: f.name, mimeType: f.type, dataBase64: b64});
		};
		reader.onerror = () => {
			setReplaceError(formatMessage({id: 'sow_upload.file_read_error'}));
			if (fileInputRef.current) fileInputRef.current.value = '';
		};
		reader.readAsDataURL(f);
	};

	const activeStep = steps.find(s => s.status === 'active');
	const allDone = steps.length > 0 && steps.every(s => s.status === 'done');

	return (
		<ExtractionBody>
			<ExtractionCard>
				<FileCard>
					<FileCardIcon>
						<Icon icon="deliverable" size="m" color="secondary" />
					</FileCardIcon>
					<FileCardMeta>
						<FileCardName title={file.filename}>{file.filename}</FileCardName>
						<FileCardSize>
							{formatBytes(file.dataBase64)} · {formatMessage({id: 'sow_upload.uploaded_just_now'})}
						</FileCardSize>
					</FileCardMeta>
					<FileCardReplace
						onClick={() => {
							setReplaceError(null);
							fileInputRef.current?.click();
						}}
					>
						{formatMessage({id: 'sow_upload.replace'})}
					</FileCardReplace>
					<input
						ref={fileInputRef}
						type="file"
						accept=".pdf,.docx,.md"
						style={{display: 'none'}}
						onChange={handleReplaceFile}
					/>
				</FileCard>
				{replaceError && <div style={{fontSize: 12, color: ERROR_COLOR_2, padding: '2px 0'}}>{replaceError}</div>}

				<ExtractionStatus>
					<SpinnerIcon>
						<Icon icon="reload" size="s" customColor={NOVA_PURPLE} />
					</SpinnerIcon>
					<Icon icon="sparkle" size="s" customColor={NOVA_PURPLE} />
					{allDone
						? formatMessage({id: 'sow_upload.nova_finalising'})
						: activeStep
						? `${formatMessage({id: 'sow_upload.nova_reading'})} ${
								SOW_CHECKLIST_LABELS[activeStep.label] ?? activeStep.label
						  }…`
						: formatMessage({id: 'sow_upload.nova_reading_sow'})}
				</ExtractionStatus>

				<ChecklistContainer>
					{steps.map(step => (
						<ChecklistRow key={step.label}>
							<ChecklistDot status={step.status}>
								{step.status === 'done' && <Icon icon="done" size="s" color="white" />}
							</ChecklistDot>
							<ChecklistLabel status={step.status}>
								{SOW_CHECKLIST_LABELS[step.label] ?? step.label}
							</ChecklistLabel>
						</ChecklistRow>
					))}
				</ChecklistContainer>
			</ExtractionCard>
		</ExtractionBody>
	);
};

export default SowExtractionProgress;
